<template>
  <div>
    <v-card v-if="isBigPost" dark flat class="post-card mt-5" :class="{ 'mx-5': !inProfile }">
      <v-list-item dark class="px-3">
        <v-list-item-avatar class="cursor-pointer" @click="viewProfile">
          <v-img
            :src="author.bild ? 'https://app.wellcomefit.ch/img/profilbilder/' + author.bild : '/img/profilbilder/uploads/1741100494649.png'"></v-img>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title class="font-weight-bold d-flex align-center card-text-color cursor-pointer" @click="viewProfile">
            {{ author.vorname }} {{ author.nachname }}
            <v-icon v-if="author.isVerified" color="primary" small class="ml-1">mdi-check-decagram</v-icon>
          </v-list-item-title>
          <v-list-item-subtitle>vor {{ formattedTimestamp }}</v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action>
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on"><v-icon class="card-text-color-secondary">mdi-dots-vertical</v-icon></v-btn>
            </template>
            <v-list dense dark>
              <v-list-item v-if="isOwnPost" @click="$emit('delete-post', item._id)">
                <v-list-item-icon><v-icon color="error">mdi-delete-outline</v-icon></v-list-item-icon>
                <v-list-item-title>Post löschen</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="!isOwnPost" @click="reportDialog = true">
                <v-list-item-icon><v-icon color="error">mdi-flag-outline</v-icon></v-list-item-icon>
                <v-list-item-title>Post melden</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-list-item-action>
      </v-list-item>

      <template v-if="item.type === 'image_post'">
        <p v-if="content.text" class="px-4 mt-2 mb-3 card-text-color">{{ content.text }}</p>

        <v-img v-if="content.imageIdentifier" v-intersect="onImageIntersect" :src="imageSrc" class="white--text align-end" max-height="600px" contain>
          <template v-slot:placeholder>
            <v-row class="fill-height ma-0" align="center" justify="center">
              <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
            </v-row>
          </template>
        </v-img>
      </template>

      <template v-else-if="item.type === 'status_update'">
        <v-card-text class="py-4">
          <p class="text-h6 white--text" style="line-height: 1.5">{{ content.text }}</p>
        </v-card-text>
      </template>

      <template v-else-if="item.type === 'training_plan_share'">
        <p class="px-4 mt-2 mb-3 card-text-color">"{{ content.text }}"</p>
        <v-card outlined class="ma-3 plan-card">
          <v-card-title class="pb-5">
            <v-icon left color="primary">mdi-clipboard-text-outline</v-icon>
            <span class="text-subtitle-1 font-weight-bold">{{ content.plan.name }}</span>
          </v-card-title>
          <v-card-subtitle>{{ content.plan.duration }} &middot; {{ content.plan.split }}</v-card-subtitle>
        </v-card>
      </template>

      <template v-else-if="item.type === 'new_pr'">
        <v-img
          height="200px"
          src="https://images.unsplash.com/photo-1594737625723-a5121734a2be?auto=format&fit=crop&w=1350"
          class="align-center text-center pr-card-bg">
          <div class="pr-overlay">
            <div class="caption">NEW PERSONAL RECORD!</div>
            <h2 class="text-h2 font-weight-black mt-2 pr-value">{{ content.pr.value }}</h2>
            <div class="text-h5 font-weight-bold">{{ content.pr.exercise }}</div>
            <p v-if="false && content.text" class="mt-4">{{ content.text }}</p>
          </div>
        </v-img>
      </template>

      <template v-else-if="item.type === 'training_partner_search'">
        <div class="pa-4 search-post-card">
          <div class="search-post-glow"></div>
          <div class="search-post-content">
            <div class="d-flex align-center mb-4">
              <v-icon size="40" class="search-post-icon mr-4">mdi-account-search-outline</v-icon>
              <div>
                <h3 class="search-post-title">Suche einen<br /><span>Lifting Partner</span></h3>
              </div>
            </div>
            <v-chip-group column>
              <v-chip label class="search-post-chip"><v-icon left small>mdi-weight-lifter</v-icon>{{ content.search.workoutType }}</v-chip>
              <v-chip label class="search-post-chip"><v-icon left small>mdi-fire</v-icon>{{ content.search.intensity }}</v-chip>
            </v-chip-group>
            <v-chip-group class="mt-2" v-if="content.search.availability">
              <v-chip v-for="day in content.search.availability.days" :key="day" label class="search-post-chip">
                <v-icon left small>mdi-calendar-blank</v-icon>{{ day }}
              </v-chip>
              <v-chip label class="search-post-chip"><v-icon left small>mdi-clock-outline</v-icon>{{ content.search.availability.timeOfDay }}</v-chip>
            </v-chip-group>

            <div class="mt-5">
              <v-btn
                v-if="!isOwnPost && !pendingRequestId && !isAlreadyPartner"
                color="primary"
                block
                depressed
                rounded
                large
                class="font-weight-bold search-post-cta"
                @click="sendRequestFromPost"
                :loading="isInteracting">
                <v-icon left>mdi-send-check-outline</v-icon>
                Anfrage senden
              </v-btn>

              <div v-if="!isOwnPost && pendingRequestId && !isAlreadyPartner" class="pending-request-box">
                <div class="d-flex align-center">
                  <v-icon class="mr-3" color="info">mdi-clock-outline</v-icon>
                  <span class="font-weight-bold">Anfrage ausstehend</span>
                  <v-spacer></v-spacer>
                  <v-btn text small @click="withdrawRequest" :loading="isInteracting"> Zurückziehen </v-btn>
                </div>
              </div>

              <div v-if="!isOwnPost && isAlreadyPartner" class="already-partners-box">
                <div class="d-flex align-center">
                  <v-icon class="mr-3" color="success">mdi-account-check-outline</v-icon>
                  <div>
                    <span class="font-weight-bold">Ihr seid bereits Partner!</span>
                    <p class="caption mb-0">Bearbeite den Plan auf dem Profil.</p>
                  </div>
                  <v-spacer></v-spacer>
                  <v-btn icon @click="viewProfile">
                    <v-icon>mdi-arrow-right-circle-outline</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="item.type === 'check_in'">
        <v-img
          height="400px"
          src="https://images.unsplash.com/photo-1571902943202-507ec2618e8f?auto=format&fit=crop&w=1350"
          class="align-center text-center">
          <div class="checkin-overlay">
            <v-icon x-large class="checkin-icon">mdi-map-marker-check</v-icon>
            <h2 class="text-h4 font-weight-bold mt-2">Checked In!</h2>
            <p v-if="content.text">"{{ content.text }}"</p>
          </div>
        </v-img>
      </template>

      <template v-else-if="item.type === 'streak_milestone'">
        <v-img
          height="400px"
          src="https://images.unsplash.com/photo-1605296867304-46d5465a13f1?auto=format&fit=crop&w=1350"
          class="align-center text-center streak-card-bg">
          <div class="streak-overlay">
            <v-icon size="80" class="fire-icon">mdi-fire</v-icon>
            <h2 class="text-h2 font-weight-black mt-2">{{ content.streak.days }} TAGE</h2>
            <div class="text-h5 font-weight-bold">STREAK!</div>
            <p class="mt-4">"{{ content.text }}"</p>
          </div>
        </v-img>
      </template>

      <template v-else-if="item.type === 'badge_unlocked'">
        <div class="text-center pa-8 badge-card-bg">
          <v-avatar size="120" class="mb-4 badge-avatar">
            <v-icon size="70" class="badge-icon">{{ content.badge.icon }}</v-icon>
          </v-avatar>
          <div class="caption">BADGE FREIGESCHALTET</div>
          <h2 class="text-h4 font-weight-bold card-text-color">{{ content.badge.name }}</h2>
          <p class="mt-2 card-text-color">"{{ content.text }}"</p>
        </div>
      </template>

      <v-card-actions class="px-2">
        <v-btn icon large @click="toggleLike" :loading="isLiking">
          <v-icon class="like-icon" :class="{ liked: isLiked }">{{ isLiked ? "mdi-heart" : "mdi-heart-outline" }}</v-icon>
        </v-btn>
        <v-btn icon large @click="openCommentDialog"><v-icon class="card-text-color-secondary">mdi-comment-outline</v-icon></v-btn>
        <v-btn icon v-if="false" large><v-icon class="card-text-color-secondary">mdi-share-variant-outline</v-icon></v-btn>
      </v-card-actions>

      <v-card-text class="px-4 pt-0">
        <p class="body-2 font-weight-bold mb-1 cursor-pointer" @click="openCommentDialog">{{ localLikesCount }} Likes</p>
        <div v-if="!['image_post', 'status_update'].includes(item.type) && content.text">
          <p class="body-2 mb-2">
            <strong class="font-weight-bold cursor-pointer" @click="viewProfile">{{ author.vorname }}</strong>
            <span> {{ content.text }}</span>
          </p>
        </div>

        <div class="caption cursor-pointer" @click="openCommentDialog">
          {{ item.comments.length > 0 ? `Alle ${item.comments.length} Kommentare ansehen` : "Kommentieren..." }}
        </div>
      </v-card-text>
    </v-card>

    <v-alert v-else dense border="left" colored-border color="primary" class="mini-post mx-4">
      <div v-if="item.type === 'new_friendship'" class="d-flex align-center">
        <v-icon left class="mr-3" color="primary">mdi-account-multiple-plus</v-icon>
        <v-avatar v-if="item.actors" size="32" class="mr-n2 cursor-pointer" @click="viewProfile(item.actors[0]._id)"
          ><img :src="item.actors[0].bild ? '/img/profilbilder/' + item.actors[0].bild : ''"
        /></v-avatar>
        <v-avatar v-if="item.actors" size="32" class="mr-3 cursor-pointer" @click="viewProfile(item.actors[1]._id)"
          ><img :src="item.actors[1].bild ? '/img/profilbilder/' + item.actors[1].bild : ''"
        /></v-avatar>
        <div v-if="item.actors" class="body-2">
          <strong class="font-weight-bold cursor-pointer" @click="viewProfile(item.actors[0]._id)">{{ item.actors[0].vorname }}</strong> und
          <strong class="font-weight-bold cursor-pointer" @click="viewProfile(item.actors[1]._id)">{{ item.actors[1].vorname }}</strong> sind jetzt
          Freunde.
        </div>
        <v-spacer></v-spacer>
        <div class="caption ml-3">{{ formattedTimestamp }}</div>
      </div>
      <div v-else-if="['course_signup', 'course_instructing'].includes(item.type)" class="d-flex align-center">
        <v-icon left class="mr-3" color="primary">{{ miniPostIcon }}</v-icon>

        <v-avatar size="32" class="mr-3 cursor-pointer" @click="viewProfile">
          <v-img
            :src="author.bild ? 'https://app.wellcomefit.ch/img/profilbilder/' + author.bild : '/img/profilbilder/uploads/1741100494649.png'"></v-img
        ></v-avatar>
        <div class="body-2">
          <strong class="font-weight-bold cursor-pointer" @click="viewProfile">{{ author.vorname }}</strong>
          <span v-if="item.type === 'course_signup'">
            hat sich für <strong>{{ item.course.name }}</strong> angemeldet.
          </span>
          <span v-else>
            unterrichtet den Kurs <strong>{{ item.course.name }}</strong
            >.
          </span>
        </div>
        <v-spacer></v-spacer>
        <div class="caption ml-3">{{ formattedTimestamp }}</div>
      </div>
    </v-alert>

    <v-dialog v-model="commentDialog" max-width="600px" scrollable>
      <v-card dark>
        <v-toolbar dense flat dark color="primary">
          <v-toolbar-title>Kommentare</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="commentDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text style="height: 400px" class="pa-0">
          <div v-if="commentsLoading" class="text-center py-16">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="comments.length === 0" class="text-center grey--text py-16">
            <p>Noch keine Kommentare.</p>
            <p>Sei der Erste!</p>
          </div>
          <v-list v-else color="transparent">
            <v-list-item v-for="comment in comments" :key="comment._id">
              <v-list-item-avatar class="cursor-pointer" @click="viewProfile(comment.user._id)">
                <v-img :src="comment.user.bild ? '/img/profilbilder/' + comment.user.bild : '/img/profilbilder/uploads/1741100494649.png'"></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="body-2" style="white-space: normal">
                  <strong class="cursor-pointer" @click="viewProfile(comment.user._id)">{{ comment.user.vorname }}</strong>
                  <span class="ml-2">{{ comment.text }}</span>
                </v-list-item-title>
                <v-list-item-subtitle>{{ formatCommentTimestamp(comment.createdAt) }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-text-field
            v-model="newComment"
            label="Kommentar hinzufügen..."
            hide-details
            filled
            rounded
            dense
            append-icon="mdi-send"
            @click:append="addComment"
            @keydown.enter="addComment"
            :loading="isCommenting"
            :disabled="isCommenting"></v-text-field>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="reportDialog" max-width="500px">
      <v-card dark>
        <v-card-title>Post melden</v-card-title>
        <v-card-text>
          <p class="body-1">Warum möchtest du diesen Post melden? Deine Meldung ist anonym.</p>
          <v-textarea v-model="reportReason" label="Grund (optional, aber hilfreich)" filled rounded auto-grow rows="3"></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="reportDialog = false">Abbrechen</v-btn>
          <v-btn color="error" @click="submitReport" :disabled="!reportReason.trim()">Melden</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import { formatDistanceToNow } from "date-fns"
import { de } from "date-fns/locale"

export default {
  props: {
    item: { type: Object, required: true },
    inProfile: { type: Boolean, default: false },
  },
  data() {
    return {
      imageSrc: null, // NEU: Initial keine Bildquelle
      isImageLoading: false, // NEU: Status für den Bild-Ladevorgang
      isLiked: false,
      isLiking: false,
      localLikesCount: 0,
      commentDialog: false,
      commentsLoading: false,
      isCommenting: false,
      reportDialog: false,
      comments: [],
      newComment: "",
      reportReason: "",
      colors: {
        peach: "#F7B5A7",
        charcoal: "#232929",
        green: "#02734A",
        offWhite: "#F7F7F4",
        lightBlue: "#C9EEF2",
        yellow: "#F2B950",
      },
      pendingRequestId: null,
      isInteracting: false,
      isAlreadyPartner: false, // Das neue Flag
    }
  },
  computed: {
    formattedAvailability() {
      const search = this.content.search
      if (!search || !search.availability) return "Flexibel"

      switch (search.availability.type) {
        case "recurring":
          return `Jeden ${search.availability.recurringDay} ${search.availability.recurringTime}`
        case "specific_date":
          return `Am ${new Date(search.availability.specificDate).toLocaleString("de-CH")}`
        case "flexible":
        default:
          return "Zeitlich flexibel"
      }
    },
    isOwnPost() {
      // Prüft, ob der eingeloggte User der Autor dieses Posts ist
      if (!this.item.author || !this.item.author._id) {
        return false
      }
      return this.item.author._id === userService.getUserId()
    },
    author() {
      return this.item.author || {}
    },
    content() {
      return this.item.content || {}
    },

    isBigPost() {
      const bigTypes = [
        "image_post",
        "status_update",
        "training_plan_share",
        "new_pr",
        "streak_milestone",
        "badge_unlocked",
        "training_partner_search",
        "check_in",
      ]
      return bigTypes.includes(this.item.type)
    },
    miniPostIcon() {
      const iconMap = {
        course_signup: "mdi-calendar-plus",
        course_instructing: "mdi-teach",
      }
      return iconMap[this.item.type] || "mdi-information-outline"
    },
    formattedTimestamp() {
      if (!this.item.timestamp) return ""
      return formatDistanceToNow(new Date(this.item.timestamp * 1000), { addSuffix: false, locale: de })
    },
  },
  methods: {
    async withdrawRequest() {
      if (!confirm("Möchtest du diese Anfrage wirklich zurückziehen?")) return
      this.isInteracting = true
      try {
        await api.social.withdrawTrainingRequest(this.pendingRequestId)
        this.pendingRequestId = null
        this.$root.$emit("show-snackbar", "Anfrage zurückgezogen.", "info")
      } catch (error) {
        alert(error.response?.data?.message || "Zurückziehen fehlgeschlagen.")
      } finally {
        this.isInteracting = false
      }
    },
    async sendRequestFromPost() {
      if (!confirm(`Möchtest du ${this.author.vorname} eine Trainingsanfrage senden?`)) return
      this.isInteracting = true
      try {
        const newRequest = await api.social.createTrainingRequest({
          recipientId: this.author._id,
          postId: this.item._id,
          availability: this.content.search.availability,
          message: `Hey, ich habe deinen Post gesehen und hätte Interesse!`,
        })
        this.pendingRequestId = newRequest._id
        this.$root.$emit("show-snackbar", "Anfrage gesendet!", "success")
      } catch (error) {
        alert(error.response?.data?.message || "Senden der Anfrage fehlgeschlagen.")
      } finally {
        this.isInteracting = false
      }
    },
    onImageIntersect(entries, observer, isIntersecting) {
      if (isIntersecting) {
        this.loadImage()
      }
    },
    async loadImage() {
      console.log("LOADING IMAGE")
      // Nur ausführen, wenn ein Identifier da ist und das Bild noch nicht geladen wurde oder gerade lädt
      if (!this.content.imageIdentifier || this.imageSrc || this.isImageLoading) {
        return
      }

      this.isImageLoading = true
      try {
        // Annahme: Du fügst eine neue Funktion in socialApi.js hinzu (siehe unten)
        const imageBlob = await api.social.getSecureImage(this.content.imageIdentifier)
        // Erstelle eine temporäre, lokale URL aus den rohen Bilddaten
        this.imageSrc = URL.createObjectURL(imageBlob)
      } catch (error) {
        console.error("Fehler beim Laden des geschützten Bildes:", error)
        // Optional: Ein Fallback-Bild bei Fehler setzen
        // this.imageSrc = '/path/to/error-image.png';
      } finally {
        this.isImageLoading = false
      }
    },
    viewProfile(userId) {
      const id = typeof userId === "string" ? userId : this.author._id
      this.commentDialog = false // Dialog schliessen, falls offen
      this.$emit("view-profile", id)
    },
    openCommentDialog() {
      this.commentDialog = true
    },
    async fetchComments() {
      if (this.comments.length > 0 && !this.isCommenting) return // Nicht erneut laden, außer wir haben gerade gepostet
      this.commentsLoading = true
      try {
        this.comments = await api.social.getComments(this.item._id)
      } catch (error) {
        console.error("Fehler beim Laden der Kommentare:", error)
      } finally {
        this.commentsLoading = false
      }
    },
    async toggleLike() {
      this.isLiking = true
      const originalLikeState = this.isLiked
      this.isLiked = !this.isLiked
      this.localLikesCount += this.isLiked ? 1 : -1
      try {
        await api.social.toggleLike(this.item._id)
      } catch (error) {
        console.error("Like fehlgeschlagen:", error)
        this.isLiked = originalLikeState
        this.localLikesCount += this.isLiked ? 1 : -1
      } finally {
        this.isLiking = false
      }
    },
    async addComment() {
      if (!this.newComment.trim() || this.isCommenting) return
      this.isCommenting = true
      try {
        const updatedComments = await api.social.addComment(this.item._id, this.newComment)
        this.comments = updatedComments
        this.newComment = ""
        this.$emit("post-updated") // Signal zum Aktualisieren des Feeds
      } catch (error) {
        console.error("Kommentar fehlgeschlagen:", error)
      } finally {
        this.isCommenting = false
      }
    },
    async submitReport() {
      if (!this.reportReason.trim()) return
      try {
        await api.social.reportItem({
          itemId: this.item._id,
          itemType: "post",
          reason: this.reportReason,
        })
        this.reportDialog = false
        this.reportReason = ""
        // Hier eine Snackbar für Erfolgsmeldung anzeigen
        this.$toast.success("Meldung erfolgreich gesendet. Danke!")
      } catch (error) {
        console.error("Meldung fehlgeschlagen:", error)
        this.$root.$emit("Meldung fehlgeschlagen.")
      }
    },
    formatCommentTimestamp(date) {
      if (!date) return ""
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: de })
    },
  },
  watch: {
    commentDialog(isOpen) {
      if (isOpen) {
        this.fetchComments()
      }
    },
  },
  created() {
    const userId = userService.getUserId()
    this.isLiked = this.item.likes?.includes(userId) ?? false
    this.localLikesCount = this.item.likes?.length ?? 0
    if (this.item.sentRequestId) {
      this.pendingRequestId = this.item.sentRequestId
    }
    if (this.item.isAlreadyPartner) {
      this.isAlreadyPartner = this.item.isAlreadyPartner
    }
  },
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.like-icon.liked {
  color: #f7b5a7 !important; /* Peach für geliktes Herz */
}
/* Farben für die visuellen Post-Typen */
.post-card,
.mini-post {
  background-color: #232929 !important;
  border: 1px solid #3a4141 !important;
  border-radius: 12px !important;
  color: #f7f7f4;
}
.card-text-color {
  color: #f7f7f4 !important;
}
.card-text-color-secondary,
.v-list-item__subtitle,
.caption {
  color: rgba(247, 247, 244, 0.7) !important;
}
.plan-card {
  background-color: rgba(247, 247, 244, 0.05) !important;
  border: 1px solid rgba(247, 247, 244, 0.1) !important;
}
.pr-card-bg::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle, transparent 0%, #232929 80%);
}
.pr-value {
  color: #f2b950;
}
.checkin-overlay {
  background: rgba(35, 41, 41, 0.6);
}
.checkin-icon {
  color: #c9eef2 !important;
}
.streak-card-bg::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle, rgba(247, 181, 167, 0.3) 0%, #232929 80%);
}
.streak-overlay .fire-icon {
  color: #f7b5a7;
  filter: drop-shadow(0 0 15px #f7b5a7);
}
.badge-card-bg {
  background: linear-gradient(135deg, #2a3131 0%, #232929 100%);
}
.badge-avatar {
  background-color: rgba(242, 185, 80, 0.1) !important;
  border: 2px solid #f2b950;
}
.badge-icon {
  color: #f2b950 !important;
}
/* *** NEUE STYLES FÜR DEN SEARCH-POST *** */
.search-post-card {
  position: relative;
  background: rgba(40, 40, 40, 0.7);
  backdrop-filter: blur(20px) saturate(180%);
  border-radius: 16px !important;
  /*border: 1px solid rgba(255, 255, 255, 0.125);*/
  overflow: hidden;
}
.search-post-glow {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 16px;
  padding: 2px;
  width: 100%;

  background-size: 300% 300%;
  animation: gradient-animation 5s ease infinite alternate;
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  z-index: 0;
}
.search-post-content {
  position: relative;
  z-index: 1;
}
.search-post-icon {
  color: #f7f7f4;
}
.search-post-title {
  font-weight: 300;
  font-size: 1.5rem;
  line-height: 1.2;
  color: rgba(255, 255, 255, 0.8);
}
.search-post-title span {
  font-weight: 800;
  color: #fff;
}
.search-post-chip {
  background-color: rgba(255, 255, 255, 0.1) !important;
  color: rgba(255, 255, 255, 0.9) !important;
  font-weight: 600;
}
.search-post-message {
  font-style: italic;
  background-color: rgba(0, 0, 0, 0.2);
  padding: 12px;
  border-radius: 8px;
}
.search-post-cta:hover {
  transform: scale(1.02);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
}

.pending-request-box {
  background-color: rgba(52, 152, 219, 0.1);
  border: 1px solid rgba(52, 152, 219, 0.3);
  border-radius: 12px;
  padding: 8px 16px;
}

.already-partners-box {
  background-color: rgba(2, 115, 74, 0.15); /* Helles Erfolgs-Grün */
  border: 1px solid rgba(2, 115, 74, 0.4);
  border-radius: 12px;
  padding: 8px 16px;
}
</style>
